/**
 * Shared filter chip primitives used by the RentOk-style filter strip
 * across Collections, Dues, Tenants, Visitors, Expenses pages.
 *
 * Components:
 *   <DateChip />     — preset date range popover (with Custom)
 *   <MultiChip />    — multi-select checkbox popover
 *   <SingleChip />   — single-select radio popover
 *   <ChipPopover />  — bare popover wrapper for custom content
 *   <FiltersSheet /> — right slide-in sheet with two-pane layout
 *
 * All chips use saffron (#EA580C) when "highlight" is true, neutral otherwise.
 */
import React, { useState, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Sheet, SheetContent } from '../ui/sheet';
import { Button } from '../ui/button';
import { ChevronDown, Calendar, ArrowLeft } from 'lucide-react';

// ----------------------------------------
// Date preset list (12 buckets) — shared
// ----------------------------------------
export const datePresets = () => {
  const today = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const last7 = new Date(today); last7.setDate(today.getDate() - 6);
  const last30 = new Date(today); last30.setDate(today.getDate() - 29);
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
  const last3 = new Date(today); last3.setMonth(today.getMonth() - 3);
  const last6 = new Date(today); last6.setMonth(today.getMonth() - 6);
  const last12 = new Date(today); last12.setMonth(today.getMonth() - 12);
  const prevYearStart = new Date(today.getFullYear() - 1, 0, 1);
  const prevYearEnd = new Date(today.getFullYear() - 1, 11, 31);
  return [
    { key: 'all',        label: 'All Time',         start: '', end: '' },
    { key: 'today',      label: 'Today',            start: fmt(today), end: fmt(today) },
    { key: 'yesterday',  label: 'Yesterday',        start: fmt(yesterday), end: fmt(yesterday) },
    { key: 'this_month', label: today.toLocaleDateString('en-US', { month: 'long' }), start: fmt(monthStart), end: fmt(monthEnd) },
    { key: 'last_7',     label: 'Last 7 Days',      start: fmt(last7), end: fmt(today) },
    { key: 'last_30',    label: 'Last 30 Days',     start: fmt(last30), end: fmt(today) },
    { key: 'prev_month', label: 'Previous Month',   start: fmt(prevMonthStart), end: fmt(prevMonthEnd) },
    { key: 'last_3m',    label: 'Last 3 Months',    start: fmt(last3), end: fmt(today) },
    { key: 'last_6m',    label: 'Last 6 Months',    start: fmt(last6), end: fmt(today) },
    { key: 'last_12m',   label: 'Last 12 Months',   start: fmt(last12), end: fmt(today) },
    { key: 'prev_year',  label: 'Previous Year',    start: fmt(prevYearStart), end: fmt(prevYearEnd) },
    { key: 'custom',     label: 'Custom Date Range', start: '', end: '' },
  ];
};

const chipBase = (highlight) =>
  `inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-full border whitespace-nowrap transition-colors ${
    highlight
      ? 'bg-orange-50 border-orange-300 text-orange-800 font-medium'
      : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
  }`;

// ----------------------------------------
// DateChip
// ----------------------------------------
export const DateChip = ({ value, onChange, label, highlight, testId }) => {
  const [open, setOpen] = useState(false);
  const presets = datePresets();
  const [custom, setCustom] = useState({ start: value.start, end: value.end });
  useEffect(() => { setCustom({ start: value.start, end: value.end }); }, [value]);
  const apply = (preset) => {
    if (preset.key === 'custom') {
      onChange({ preset: 'custom', start: custom.start || '', end: custom.end || '' });
    } else {
      onChange({ preset: preset.key, start: preset.start, end: preset.end });
    }
    setOpen(false);
  };
  const applyCustom = () => {
    onChange({ preset: 'custom', start: custom.start, end: custom.end });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={testId} className={chipBase(highlight)}>
          <Calendar size={12} /> {label}
          <ChevronDown size={12} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <div className="max-h-80 overflow-y-auto">
          {presets.map(p => (
            <button
              key={p.key}
              data-testid={testId ? `${testId}-preset-${p.key}` : undefined}
              onClick={() => apply(p)}
              className={`w-full text-left text-sm px-2.5 py-1.5 rounded hover:bg-orange-50 ${
                value.preset === p.key ? 'bg-orange-100 text-orange-800 font-medium' : 'text-gray-700'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {value.preset === 'custom' && (
          <div className="border-t mt-2 pt-2 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <input type="date" value={custom.start}
                onChange={(e) => setCustom(c => ({ ...c, start: e.target.value }))}
                className="text-xs border border-gray-300 rounded px-2 py-1" />
              <input type="date" value={custom.end}
                onChange={(e) => setCustom(c => ({ ...c, end: e.target.value }))}
                className="text-xs border border-gray-300 rounded px-2 py-1" />
            </div>
            <Button size="sm" className="w-full bg-orange-600 hover:bg-orange-700 text-white" onClick={applyCustom}>
              Apply Custom Range
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

// ----------------------------------------
// MultiChip
// ----------------------------------------
export const MultiChip = ({ label, options, selected, onChange, highlight, testId }) => {
  const [open, setOpen] = useState(false);
  const toggle = (key) => onChange(selected.includes(key) ? selected.filter(k => k !== key) : [...selected, key]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={testId} className={chipBase(highlight)}>
          {label}
          <ChevronDown size={12} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1" align="start">
        {options.length === 0 ? (
          <p className="text-xs text-gray-500 px-2 py-3 text-center">No options</p>
        ) : (
          options.map(opt => (
            <label key={opt.key} className="flex items-center gap-2 px-2.5 py-1.5 rounded hover:bg-orange-50 cursor-pointer text-sm text-gray-700">
              <input
                type="checkbox"
                checked={selected.includes(opt.key)}
                onChange={() => toggle(opt.key)}
                className="accent-orange-600"
              />
              {opt.label}
            </label>
          ))
        )}
        {selected.length > 0 && (
          <button onClick={() => onChange([])} className="w-full text-center text-xs text-gray-500 hover:text-orange-700 py-1.5 border-t mt-1">
            Clear
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
};

// ----------------------------------------
// SingleChip (radio)
// ----------------------------------------
export const SingleChip = ({ label, options, value, onChange, highlight, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={testId} className={chipBase(highlight)}>
          {label}
          <ChevronDown size={12} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1" align="start">
        {options.map(opt => (
          <button
            key={opt.key}
            onClick={() => { onChange(opt.key); setOpen(false); }}
            className={`w-full text-left text-sm px-2.5 py-1.5 rounded hover:bg-orange-50 ${
              value === opt.key ? 'bg-orange-100 text-orange-800 font-medium' : 'text-gray-700'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};

// ----------------------------------------
// FiltersSheet — right slide-in with 2-pane
// sections: [{ key, label, kind: 'date'|'multi'|'single', options? }]
// values:   { sectionKey: dateRange|array|string }
// ----------------------------------------
export const FiltersSheet = ({
  open, onClose, title = 'Filters',
  sections, values, onApply, onClear, defaultActive,
}) => {
  const [draft, setDraft] = useState(values);
  const [active, setActive] = useState(defaultActive || sections[0]?.key);
  useEffect(() => { if (open) { setDraft(values); setActive(defaultActive || sections[0]?.key); } }, [open, values, defaultActive, sections]);

  const setKey = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const apply = () => { onApply(draft); onClose(); };
  const clearAndClose = () => { onClear(); onClose(); };

  const selectedCount = (k) => {
    const v = draft[k];
    if (Array.isArray(v)) return v.length;
    if (v && typeof v === 'object' && v.preset && v.preset !== 'all') return 1;
    if (typeof v === 'string' && v && v !== 'all') return 1;
    return 0;
  };

  const section = sections.find(s => s.key === active) || sections[0];

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col" data-testid="filters-sheet">
        <div className="flex items-center gap-2 px-4 py-3 border-b">
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><ArrowLeft size={16} /></button>
          <h2 className="text-base font-semibold">{title}</h2>
        </div>
        <div className="flex flex-1 overflow-hidden">
          <div className="w-44 sm:w-52 border-r bg-gray-50 overflow-y-auto">
            {sections.map(s => {
              const cnt = selectedCount(s.key);
              const a = active === s.key;
              return (
                <button key={s.key} onClick={() => setActive(s.key)}
                  className={`w-full text-left px-3 py-3 text-sm border-l-4 ${
                    a ? 'bg-white border-orange-600 text-gray-900 font-medium' : 'border-transparent text-gray-600 hover:bg-white'
                  }`}
                >
                  <div>{s.label}</div>
                  {cnt > 0 && <div className="text-[10px] text-orange-700 mt-0.5">{cnt} selected</div>}
                </button>
              );
            })}
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">{section.label}</p>
            {section.kind === 'date' && (
              <div className="space-y-1">
                {datePresets().map(p => (
                  <label key={p.key} className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer hover:bg-orange-50 ${draft[section.key]?.preset === p.key ? 'bg-orange-50' : ''}`}>
                    <input type="checkbox" checked={draft[section.key]?.preset === p.key}
                      onChange={() => setKey(section.key, { preset: p.key, start: p.start, end: p.end })}
                      className="accent-orange-600" />
                    {p.label}
                  </label>
                ))}
              </div>
            )}
            {section.kind === 'multi' && (
              <div className="space-y-1">
                {(section.options || []).map(opt => {
                  const sel = (draft[section.key] || []).includes(opt.key);
                  return (
                    <label key={opt.key} className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer hover:bg-orange-50 ${sel ? 'bg-orange-50' : ''}`}>
                      <input type="checkbox" checked={sel}
                        onChange={() => {
                          const cur = draft[section.key] || [];
                          setKey(section.key, sel ? cur.filter(x => x !== opt.key) : [...cur, opt.key]);
                        }}
                        className="accent-orange-600" />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
            )}
            {section.kind === 'single' && (
              <div className="space-y-1">
                {(section.options || []).map(opt => (
                  <label key={opt.key} className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer hover:bg-orange-50 ${draft[section.key] === opt.key ? 'bg-orange-50' : ''}`}>
                    <input type="radio" checked={draft[section.key] === opt.key}
                      onChange={() => setKey(section.key, opt.key)}
                      className="accent-orange-600" />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="border-t p-3 flex gap-3">
          <Button variant="outline" className="flex-1" onClick={clearAndClose}>Clear All</Button>
          <Button className="flex-1 bg-orange-600 hover:bg-orange-700 text-white" onClick={apply}>Apply</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Filter button (the "Filters" pill that opens the sheet)
export const FilterButton = ({ onClick, count, testId }) => (
  <button
    data-testid={testId || 'open-filters'}
    onClick={onClick}
    className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full border border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100 whitespace-nowrap"
  >
    Filters
    {count > 0 && (
      <span className="ml-0.5 inline-flex items-center justify-center w-5 h-5 text-[10px] rounded-full bg-orange-600 text-white font-bold">
        {count}
      </span>
    )}
  </button>
);

// Helper to build a date label from a {preset, start, end} value
export const dateChipLabel = (value, fallback = 'Date Range') => {
  if (!value || !value.preset || value.preset === 'all') return fallback;
  if (value.preset === 'custom') return `${value.start || '…'} → ${value.end || '…'}`;
  const p = datePresets().find(x => x.key === value.preset);
  if (p?.start && p?.end) {
    const fmt = (s) => new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${fmt(p.start)} – ${fmt(p.end)}`;
  }
  return p?.label || fallback;
};
